import { Button } from "@/lib/components/Button/Button";
import { Input } from "@/lib/components/Input";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import FileUpload from "@/lib/components/FileUpload";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/lib/components/Form";
import Toast from "@/lib/components/Toast";
import TagsInput from "@/lib/components/TagsInput";
import { useEffect } from "react";
import { UploadBackgroundMusicDto } from "@repo/api/background-music";
import { useBackgroundMusicsByExtIdsQuery } from "@/lib/queries/background-music-queries";
import { decodeAudioPeaks } from "@/lib/audio-peaks";

const FormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  genres: z.array(z.string()),
  moods: z.array(z.string()),
  tags: z.array(z.string()),
  track: z.instanceof(File).optional(),
});

type FormValues = z.infer<typeof FormSchema>;

export default function AddOrEditTrackForm({
  initialTrack,
  onInitialTrackConsumed,
}: {
  initialTrack?: File | null;
  onInitialTrackConsumed?: () => void;
} = {}) {
  const uploadBackgroundMusic = useEditorStore(
    (state) => state.service.uploadBackgroundMusic,
  );
  const updateBackgroundMusic = useEditorStore(
    (state) => state.service.updateBackgroundMusic,
  );
  const setBackgroundMusicView = useEditorStore(
    (state) => state.service.setBackgroundMusicView,
  );
  const currentView = useEditorStore(
    (state) => state.backgroundMusicDialogState,
  );
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const editingTrackExternalId = currentView.editingTrackExternalId;
  const { data: editingTracks = [] } = useBackgroundMusicsByExtIdsQuery(
    teamExternalId,
    editingTrackExternalId ? [editingTrackExternalId] : [],
  );
  const trackToEdit = editingTracks[0];

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      track: undefined,
      genres: [],
      moods: [],
      tags: [],
    },
  });

  useEffect(() => {
    if (trackToEdit) {
      form.reset({
        name: trackToEdit.name,
        genres: trackToEdit.metadata?.genres || [],
        moods: trackToEdit.metadata?.moods || [],
        tags: trackToEdit.metadata?.tags || [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTrackExternalId]);

  useEffect(() => {
    if (!initialTrack) return;
    if (editingTrackExternalId) {
      form.setValue("track", initialTrack, { shouldDirty: true });
    } else {
      const fallbackName =
        initialTrack.name.replace(/\.[^/.]+$/, "") || "Untitled track";
      form.reset({
        name: fallbackName,
        track: initialTrack,
        genres: [],
        moods: [],
        tags: [],
      });
    }
    onInitialTrackConsumed?.();
  }, [initialTrack, editingTrackExternalId, form, onInitialTrackConsumed]);

  const { isSubmitting } = form.formState;

  async function onSubmit(data: FormValues) {
    try {
      const decoded = data.track
        ? await decodeAudioPeaks(data.track)
        : undefined;
      const peaks = decoded ? JSON.stringify(decoded.peaks) : undefined;

      const formattedData: UploadBackgroundMusicDto = {
        ...data,
        genres: JSON.stringify(data.genres),
        moods: JSON.stringify(data.moods),
        tags: JSON.stringify(data.tags),
        durationMs: decoded?.durationMs,
        peaks,
      };

      if (editingTrackExternalId) {
        await updateBackgroundMusic(
          editingTrackExternalId,
          formattedData,
          data.track,
        );
        Toast.info("Track updated successfully");
      } else {
        if (!data.track) {
          form.setError("track", {
            type: "manual",
            message: "A track file is required.",
          });
          return;
        }

        await uploadBackgroundMusic(formattedData, data.track);
        Toast.info("Track uploaded successfully");
      }
      setBackgroundMusicView("LIST");
      form.reset();
    } catch (error) {
      console.error(error);
      Toast.error(
        `Failed to ${editingTrackExternalId ? "update" : "upload"} track`,
      );
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex-1 space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="track"
          render={({ field }) => (
            <FormItem className="my-4">
              <FormLabel>Track</FormLabel>
              <FormControl>
                <FileUpload
                  multiple={false}
                  accept={{ "audio/*": [".mp3", ".wav", ".ogg"] }}
                  value={field.value ? [field.value] : null}
                  onDrop={(files) => field.onChange(files?.[0] ?? null)}
                  isReplace={!!editingTrackExternalId}
                  className="min-h-40"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="genres"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Genres</FormLabel>
              <FormControl>
                <TagsInput
                  {...field}
                  placeholder="Add genres and press enter..."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="moods"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Moods</FormLabel>
              <FormControl>
                <TagsInput
                  {...field}
                  placeholder="Add moods and press enter..."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="tags"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tags</FormLabel>
              <FormControl>
                <TagsInput
                  {...field}
                  placeholder="Add tags and press enter..."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
      <div className="mt-8 flex shrink-0 basis-auto flex-wrap gap-3">
        <Button
          onClick={form.handleSubmit(onSubmit)}
          disabled={isSubmitting}
          isLoading={isSubmitting}
        >
          {editingTrackExternalId ? "Save changes" : "Add track"}
        </Button>
        <Button
          variant="outline"
          onClick={() => setBackgroundMusicView("LIST")}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </Form>
  );
}
